import React, { useEffect, useState } from 'react';
import {
  BrowserRouter, Routes, Route, Navigate,
  useParams, useNavigate,
} from 'react-router-dom';
import { GoogleAuthProvider } from '@questlabs/auth-js';
import { AuthProvider } from './context/AuthContext';
import { CurrencyProvider } from './context/CurrencyContext';
import { EcommerceProvider } from './context/EcommerceContext';
import { VendorProvider } from './context/VendorContext';
import { BackendStatusProvider } from './context/BackendStatusContext';
import ScrollToTop from './components/Utils/ScrollToTop';
import Navbar from './components/Layout/Navbar';
import Footer from './components/Layout/Footer';
import MaintenanceBanner from './components/Layout/MaintenanceBanner';
import LaunchOfferBanner from './components/Layout/LaunchOfferBanner';
import BackendStatusBanner from './components/Common/BackendStatusBanner';
import ErrorBoundary from './components/Common/ErrorBoundary';
import ImpersonationBar from './components/Dashboard/ImpersonationBar';
import ImpersonationPreviewPanel from './components/Dashboard/ImpersonationPreviewPanel';
import ProtectedRoute from './components/Common/ProtectedRoute';

import Home from './pages/Home';
import Explore from './pages/Explore';
import MemorialDetail from './pages/MemorialDetail';
import Marketplace from './pages/Marketplace';
import ProductDetail from './pages/ProductDetail';
import Resources from './pages/Resources';
import Locate from './pages/Locate';
import LiveEvents from './pages/LiveEvents';
import LiveStreamView from './pages/LiveStreamView';
import ReplayLibrary from './pages/ReplayLibrary';
import Webinars from './pages/Webinars';
import WebinarLobby from './pages/WebinarLobby';
import EventLanding from './pages/EventLanding';
import UserDashboard from './pages/UserDashboard';
import Dashboard from './pages/Dashboard';
import CreatorAdmin from './pages/CreatorAdmin';
import MerchantPortal from './pages/MerchantPortal';
import LegacyChain from './pages/LegacyChain';
import Login from './pages/Login';
import OrderTracking from './pages/OrderTracking';
import GuidedLifeInterviews from './features/interviews/GuidedLifeInterviews';

import ProgrammeGate from './features/programme/ProgrammeGate';
import ProgrammeHome from './features/programme/ProgrammeHome';
import ProgrammeEditor from './features/programme/ProgrammeEditor';
import ProgrammeReader from './features/programme/ProgrammeReader';
import ProgrammeSignIn from './features/programme/ProgrammeSignIn';
import ProgrammeCheck from './features/programme/ProgrammeCheck';

import { mockMemorials } from './data/mockData';
import {
  resolveShortCode,
  trackShortLinkClick,
  nameToSlug,
} from './services/memorialLinkService';

import { ensureDefaultPlansSeeded } from './services/plansService';

// Old hash-style links (/#/memorial/x) still resolve after the move to path routing.
const LegacyHashRedirect = () => {
  const navigate = useNavigate();
  useEffect(() => {
    const { hash } = window.location;
    if (hash.startsWith('#/')) navigate(hash.slice(1), { replace: true });
  }, [navigate]);
  return null;
};

const ShortLinkRedirect = () => {
  const { shortCode } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('resolving');

  useEffect(() => {
    let cancelled = false;
    const go = async () => {
      const record = await resolveShortCode(shortCode);
      if (cancelled) return;
      if (record?.memorial_id) {
        let destination = null;
        if (record.slugUrl) {
          const hashIndex = record.slugUrl.indexOf('/#/');
          if (hashIndex !== -1) destination = record.slugUrl.slice(hashIndex + 2);
        }
        if (!destination) {
          let slug = record.slug?.trim() || '';
          const memorialId = record.memorial_id;
          if (!slug) {
            const memorialName = record.memorial_name?.trim() || '';
            if (memorialName) slug = nameToSlug(memorialName);
            else {
              const found = mockMemorials.find(m => m.id === memorialId);
              if (found) slug = nameToSlug(found.name);
            }
          }
          if (!slug) slug = memorialId;
          destination = `/memorial/${slug}?id=${encodeURIComponent(memorialId)}`;
        }
        navigate(destination, { replace: true });
        trackShortLinkClick(record.id, record.click_count ?? 0);
        return;
      }
      if (!cancelled) {
        setStatus('notfound');
        setTimeout(() => { if (!cancelled) navigate('/explore', { replace: true }); }, 2000);
      }
    };
    go();
    return () => { cancelled = true; };
  }, [shortCode, navigate]);

  if (status === 'notfound') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-light">
        <div className="text-center space-y-4 px-6">
          <div className="text-5xl">🔍</div>
          <p className="text-accent-navy font-serif text-2xl">Link not found</p>
          <p className="text-text-muted text-sm">Redirecting you to Explore…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-light">
      <div className="text-center space-y-5">
        <div className="w-14 h-14 border-4 border-accent-gold/20 border-t-accent-gold rounded-full animate-spin mx-auto" />
        <div>
          <p className="text-accent-navy font-serif text-2xl mb-1">Finding memorial…</p>
          <p className="text-text-muted text-sm font-medium">Following shared link</p>
        </div>
      </div>
    </div>
  );
};

const AppRoot = () => {
  useEffect(() => {
    // Seed default plans if they don't exist
    ensureDefaultPlansSeeded();
  }, []);

  return (
    <ErrorBoundary>
      <GoogleAuthProvider>
        <BackendStatusProvider>
          <AuthProvider>
            <CurrencyProvider>
              <EcommerceProvider>
                <VendorProvider>
                  <BrowserRouter>
                    <LegacyHashRedirect />
                    <ScrollToTop />
                    <MaintenanceBanner />
                    <BackendStatusBanner />
                    <ImpersonationBar />
                    <ImpersonationPreviewPanel />
                    <Navbar />
                    <LaunchOfferBanner />
                    <main>
                      <Routes>
                        <Route path="/" element={<Home />} />
                        <Route path="/explore" element={<Explore />} />
                        <Route path="/memorial/:id" element={<MemorialDetail />} />
                        <Route path="/m/:shortCode" element={<ShortLinkRedirect />} />
                        <Route path="/marketplace" element={<Marketplace />} />
                        <Route path="/product/:id" element={<ProductDetail />} />
                        <Route path="/orders" element={<ProtectedRoute><OrderTracking /></ProtectedRoute>} />
                        <Route path="/resources" element={<Resources />} />
                        <Route path="/guided-life-interviews" element={<GuidedLifeInterviews />} />
                        <Route path="/locate" element={<Locate />} />
                        <Route path="/live-events" element={<LiveEvents />} />
                        <Route path="/live/:id" element={<LiveStreamView />} />
                        <Route path="/replays" element={<ReplayLibrary />} />
                        <Route path="/webinars" element={<Webinars />} />
                        <Route path="/webinar/:id" element={<WebinarLobby />} />
                        <Route path="/event/:id" element={<EventLanding />} />
                        <Route path="/login" element={<Login />} />
                        <Route path="/user/dashboard" element={<ProtectedRoute><UserDashboard /></ProtectedRoute>} />
                        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                        <Route path="/creator-admin" element={<ProtectedRoute><CreatorAdmin /></ProtectedRoute>} />
                        <Route path="/merchant" element={<ProtectedRoute><MerchantPortal /></ProtectedRoute>} />
                        <Route path="/legacy-chain" element={<LegacyChain />} />
                        <Route path="/programme/sign-in-check" element={<ProgrammeSignIn />} />
                        <Route path="/programme-check" element={<ProgrammeCheck />} />
                        <Route path="/programme" element={<ProgrammeGate><ProgrammeSignIn><ProgrammeHome /></ProgrammeSignIn></ProgrammeGate>} />
                        <Route path="/programme/edit/:id" element={<ProgrammeGate><ProgrammeSignIn><ProgrammeEditor /></ProgrammeSignIn></ProgrammeGate>} />
                        <Route path="/programme/:shareSlug" element={<ProgrammeGate><ProgrammeReader /></ProgrammeGate>} />
                        <Route path="*" element={<Navigate to="/" replace />} />
                      </Routes>
                    </main>
                    <Footer />
                  </BrowserRouter>
                </VendorProvider>
              </EcommerceProvider>
            </CurrencyProvider>
          </AuthProvider>
        </BackendStatusProvider>
      </GoogleAuthProvider>
    </ErrorBoundary>
  );
};

const App = () => <AppRoot />;

export default App;